import { CardForm } from '@/uiComponents/common';
import WorkCredentialImage from './credentialImage';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import BadgeNew from '../../../uiComponents/common/badge/badge';
import { Button } from '@/components/ui/button';
import { Check, RefreshCcw, X } from 'lucide-react';
import { useState } from 'react';
import { useUser } from '@/hooks';
import { hasAllowedRoles } from '@/helpers/roles';
import ModalEvidences from './modalEvidences';

const getStatusBadge = (status, t) => {
  switch (status) {
    case 'APPROVED':
      return (
        <BadgeNew text={t('approved.badge')} type={'green'} icon={'checked'} className='fw-600' />
      );
    case 'REVALIDATION':
      return <BadgeNew text={t('revalidate.badge')} type={'blue'} icon={''} className='fw-600' />;
    case 'REJECTED':
      return <BadgeNew text={t('rejected.badge')} type={'red'} icon={''} className='fw-600' />;
    default:
      return <BadgeNew text={t('pending.badge')} type={'orange'} />;
  }
};

const getStatusText = (status, t) => {
  switch (status) {
    case 'APPROVED':
      return t('approved.text');
    case 'REJECTED':
      return t('rejected.text');
    case 'REVALIDATION':
      return t('revalidate.text');
    default:
      return null;
  }
};

const EvidenceCard = ({ t, className, mode, id, refetchStatus, i18n }) => {
  const workCredential = useWatch({ name: 'workCredentialImage' });
  const name = useWatch({ name: 'firstName' });
  const methods = useFormContext();
  const affiliateType = useWatch({ name: 'affiliateType' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const { roles } = useUser();

  const handleApproveClick = () => {
    setPendingAction('approve');
    setIsModalOpen(true);
  };

  const handleRejectClick = () => {
    setPendingAction('reject');
    setIsModalOpen(true);
  };

  const handleRevalidateClick = () => {
    setPendingAction('revalidate');
    setIsModalOpen(true);
  };

  const workCredentialStatus = useWatch({ name: 'workCredentialStatus' });

  const handleSetValue = (variables) => {
    methods.setValue('workCredentialStatus', variables?.workCredentialStatus);
  };

  const canApprove = hasAllowedRoles(roles, [
    'superadministrator',
    'marketadmin',
    'affiliatecenter',
  ]);

  return (
    <>
      {mode === 'CONCIERGE' &&
      (affiliateType === 'CONCIERGE' || affiliateType === 'INTERNAL_STAFF') &&
      workCredentialStatus !== 'REJECTED' ? (
        <CardForm
          title={
            <>
              {t('documentationProfessional.title')}{' '}
              <span className='tw-ml-2'>{getStatusBadge(workCredentialStatus, t)}</span>
            </>
          }
          className={className}
        >
          <div className='tw-mb-6'>
            <div>
              {workCredential?.length > 0 ? (
                <Carousel>
                  <CarouselContent className='!tw-ml-0 !tw-gap-4'>
                    {workCredential?.map((w) => (
                      <CarouselItem key={w} className='!tw-basis-auto !tw-pl-0'>
                        <WorkCredentialImage
                          t={t}
                          style={{ width: '250px', height: '250px' }}
                          url={w}
                          name={name}
                        />
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  {workCredential?.length > 1 ? (
                    <>
                      <CarouselPrevious />
                      <CarouselNext />
                    </>
                  ) : null}
                </Carousel>
              ) : (
                <div>{t('workCredential.noImage')}</div>
              )}
            </div>
            <div className='tw-mt-3  tw-pt-2'>
              {workCredentialStatus !== 'PENDING' ? (
                <p className='tw-text-sm tw-text-muted-foreground !tw-mb-3'>
                  {getStatusText(workCredentialStatus, t)}
                </p>
              ) : null}
              <div className='tw-flex tw-flex-wrap tw-gap-2'>
                {workCredentialStatus !== 'APPROVED' && canApprove ? (
                  <Button
                    onClick={handleApproveClick}
                    size='sm'
                    type='button'
                    className='tw-bg-black hover:tw-bg-black/90'
                  >
                    <Check className='tw-w-4 tw-h-4 tw-mr-2' />
                    {t('approved.button')}
                  </Button>
                ) : null}
                {workCredentialStatus !== 'REJECTED' && canApprove ? (
                  <Button
                    onClick={handleRejectClick}
                    size='sm'
                    variant='outline'
                    type='button'
                    className='tw-bg-transparent'
                  >
                    <X className='tw-w-4 tw-h-4 tw-mr-2' />
                    {t('rejected.button')}
                  </Button>
                ) : null}
                {workCredentialStatus !== 'REVALIDATION' &&
                workCredential?.length > 0 &&
                canApprove ? (
                  <Button
                    onClick={handleRevalidateClick}
                    size='sm'
                    variant='outline'
                    type='button'
                    className='tw-bg-transparent'
                  >
                    <RefreshCcw className='tw-w-4 tw-h-4 tw-mr-2' />
                    {t('revalidate.button')}
                  </Button>
                ) : null}
              </div>
            </div>
            <ModalEvidences
              t={t}
              id={id}
              isModalOpen={isModalOpen}
              i18n={i18n}
              pendingAction={pendingAction}
              setIsModalOpen={setIsModalOpen}
              refetchStatus={refetchStatus}
              affiliateType={affiliateType}
              handleSetValue={handleSetValue}
              setPendingAction={setPendingAction}
            />
          </div>
        </CardForm>
      ) : null}
    </>
  );
};

export default EvidenceCard;
